import { forceArray, humanCase, isArray, naturalSortByProperty } from '@paragon/utilities';

import { ModelChildLoader, ModelSchema } from '../../index';
import { GenericModelSchema } from '../ModelSchema';
import { ROW_ID_LEVEL_SEPARATOR } from './PathUtilities';

export function isContainerRow(row: Row): row is ContainerRow {
  return row.type === 'container';
}

export function isDataRow<T>(row: Row): row is DataRow<T> {
  return typeof row.type === 'string' && row.type !== 'container';
}

function shouldContainerize(
  parentSchema: GenericModelSchema,
  childSchema: GenericModelSchema,
  containers: readonly string[],
) {
  return containers.some((spec) =>
    spec.includes('.')
      ? spec.startsWith(parentSchema.type) && spec.endsWith(childSchema.type)
      : spec === childSchema.type,
  );
}

export interface Breadcrumb {
  type: string;
  identifier: string;
}

export abstract class Row {
  // NOTE: `Row`s are cached. If you add any new data to `Row`s, make sure to update
  // `CachedRowCalculator.getCachedRowWithMetadataIfRowEqual` appropriately.

  type: string;
  abstract name: string;
  abstract childrenType: string | null;

  level: number;

  parent: Row | undefined;

  path: Breadcrumb[] = [];

  protected constructor(type: string, parent?: Row) {
    this.type = type;
    this.level = parent ? parent.level + 1 : 0;
    this.parent = parent;
  }

  abstract get rowId(): string;

  abstract generateBreadcrumb(): Breadcrumb;
  protected initializeBreadcrumbs(parent?: Row) {
    if (parent) {
      this.path = [...parent.path];
    }

    this.path.push(this.generateBreadcrumb());
  }

  get ancestors(): Row[] {
    return this.parent ? [this.parent, ...this.parent.ancestors] : [];
  }
}

interface DataRowOptions {
  readonly parent?: Row;
  readonly containers?: readonly string[];
  readonly preSortedChildrenTypes?: readonly string[];
}

export class DataRow<T> extends Row {
  // NOTE: `DataRow`s are cached. If you add any new data to `DataRow`s, make sure to update
  // `CachedRowCalculator.getCachedRowWithMetadataIfRowEqual` appropriately.

  name: string;
  id: string;
  item: T;
  schema: ModelSchema<T>;
  childrenType: string | null;
  preSortedChildrenTypes: readonly string[];

  constructor(item: T, schema: ModelSchema<T>, options: DataRowOptions = {}) {
    super(schema.type, options.parent);

    this.name = getDisplayNameForItem(item, schema);
    this.id = ((item as { guid?: string }).guid || (item as { id?: string }).id)!;
    this.item = item;
    this.schema = schema;
    const childrenSchemas = Object.values(schema.children || {});
    this.childrenType =
      childrenSchemas.length === 1 &&
      options.containers != null &&
      !shouldContainerize(schema, childrenSchemas[0], options.containers)
        ? childrenSchemas[0].type
        : null;
    this.preSortedChildrenTypes = options.preSortedChildrenTypes ?? [];

    this.initializeBreadcrumbs(options.parent);
  }

  static fromArray<T>(items: readonly T[], schema: ModelSchema<T>, options?: DataRowOptions) {
    return items.map((item) => new DataRow(item, schema, options));
  }

  get rowId(): string {
    return this.path.map((breadcrumb) => breadcrumb.identifier).join(ROW_ID_LEVEL_SEPARATOR);
  }

  generateBreadcrumb(): Breadcrumb {
    return {
      type: this.type,
      identifier: this.id,
    };
  }
}

export interface ContainerRowOptions {
  readonly parent?: Row;
}

export class ContainerRow extends Row {
  // NOTE: `ContainerRow`s are cached. If you add any new data to `ContainerRow`s, make sure to update
  // `CachedRowCalculator.getCachedRowWithMetadataIfRowEqual` appropriately.

  name: string;
  childrenCount: number;
  childrenType: string;

  private readonly pluralName: string;

  constructor(childType: GenericModelSchema, childrenCount: number, options: ContainerRowOptions = {}) {
    super('container', options.parent);

    this.childrenCount = childrenCount;
    this.childrenType = childType.type;
    this.pluralName = childType.pluralDisplayName;
    this.name = `${this.pluralName} (${this.childrenCount})`;

    this.initializeBreadcrumbs(options.parent);
  }

  get rowId(): string {
    const identifier = this.path.map((breadcrumb) => breadcrumb.identifier).join(ROW_ID_LEVEL_SEPARATOR);

    return `${identifier}Container`;
  }

  generateBreadcrumb(): Breadcrumb {
    return {
      type: this.type,
      identifier: this.childrenType,
    };
  }
}

export function getDisplayNameForItem<T>(item: T, schema: ModelSchema<T>) {
  switch (typeof schema.displayName) {
    case 'function':
      return schema.displayName(item);
    case 'string':
      return schema.displayName;
    // no default
  }

  if ((item as { name?: string }).name) {
    return `${humanCase(schema.type)} ${(item as { name: string }).name}`;
  }

  return humanCase(schema.type);
}

function sortByName(rows: readonly DataRow<unknown>[]) {
  return naturalSortByProperty(rows, (row) => row.name);
}

/**
 * Counts the given items plus, for self-nested schemas (e.g. truss groups), all their same-type descendants, so that
 * container rows report the full recursive count rather than just the top level.
 */
function countItemsOfSchema(items: readonly unknown[], schema: GenericModelSchema, loaders: ModelChildLoader): number {
  const selfNested = schema.children && Object.values(schema.children).includes(schema);
  if (!selfNested) return items.length;

  let count = items.length;
  for (const item of items) {
    const children = loaders.getChildrenOf(item, schema);
    if (Array.isArray(children)) count += countItemsOfSchema(children, schema, loaders);
  }

  return count;
}

export function getChildrenOfDataRow(
  dataRow: DataRow<unknown>,
  { loaders, containers, containerLikeTypes }: RowDerivationContext,
): RowGroup[] {
  if (!dataRow.schema.children) return [];

  return Object.values(dataRow.schema.children).map((childSchema) => {
    const children = loaders.getChildrenOf(dataRow.item, childSchema);

    if (!Array.isArray(children)) {
      return new DataRow(children, childSchema, { containers, parent: dataRow });
    }

    const childrenNeedContainer = containers && shouldContainerize(dataRow.schema, childSchema, containers);

    const parentRow = childrenNeedContainer
      ? new ContainerRow(childSchema, countItemsOfSchema(children, childSchema, loaders), { parent: dataRow })
      : dataRow;

    let childRows = DataRow.fromArray(children, childSchema, { containers, parent: parentRow });

    const sortingDesired = childrenNeedContainer || containerLikeTypes?.includes(dataRow.type);
    const preSorted = dataRow.preSortedChildrenTypes.includes(childSchema.type) || Boolean(childSchema.preSorted);

    if (sortingDesired && !preSorted) {
      childRows = sortByName(childRows);
    }

    return parentRow instanceof ContainerRow ? { containerRow: parentRow, dataRows: childRows } : childRows;
  });
}

/**
 * All the data needed for `getChildrenOfDataRow` to derive the child rows of a given row.
 */
export interface RowDerivationContext {
  readonly loaders: ModelChildLoader;

  readonly containers?: readonly string[];

  // Data rows sometimes act as containers (e.g. buildings and levels in a project). This option allows such rows to
  //   opt-in to container-like treatment such as automatic sorting of children.
  readonly containerLikeTypes?: readonly string[];
}

export interface RowWithMetadata {
  readonly row: Row;

  // update `rowMetadataEquals` if adding any new metadata fields

  readonly expandable: boolean;
  readonly indentationLevel: number;
  readonly isVisible: boolean;
}

function rowMetadataEquals(a: RowWithMetadata, b: RowWithMetadata): boolean {
  return a.expandable === b.expandable && a.indentationLevel === b.indentationLevel && a.isVisible === b.isVisible;
}

/**
 * This type exists to correlate a `ContainerRow` with its `DataRow` children during row calculation, which avoids the
 * need for a `ContainerRow` to store a reference to its children (and is important to avoid unnecessary cache
 * invalidation and re-rendering of rows).
 */
interface RowGroupWithContainer {
  readonly containerRow: ContainerRow;
  readonly dataRows: readonly DataRow<unknown>[];
}

/**
 * A group of rows, that are often of the same type.
 *
 * Some groups of rows do not have a container, so this type allows for an array of `DataRow`s or a single `DataRow`
 * without a `ContainerRow` for convenience (see `getChildrenOfDataRow` and `ExplorerPanel.getTopLevelRow`).
 */
export type RowGroup = RowGroupWithContainer | readonly DataRow<unknown>[] | DataRow<unknown>;

const isRowGroupWithContainer = (rowGroup: RowGroup): rowGroup is RowGroupWithContainer =>
  !!(rowGroup as RowGroupWithContainer).containerRow;

const getContainerRow = (rowGroup: RowGroup) => (isRowGroupWithContainer(rowGroup) ? rowGroup.containerRow : null);

const getDataRows = (rowGroup: RowGroup): readonly DataRow<unknown>[] =>
  isArray(rowGroup) ? rowGroup : isRowGroupWithContainer(rowGroup) ? rowGroup.dataRows : [rowGroup];

/**
 * This function generates a flat array of all rows that can be calculated from the given `topLevelRowGroup`, along with
 * the metadata needed to render them easily with our grid implementations.
 *
 * The results are not cached, `CachedRowCalculator` wraps this function with caching to avoid unnecessary re-rendering
 * of rows.
 *
 * @param topLevelRowGroup The top level rows in the grid; all descendants of this row will be calculated.
 * @param context The data needed by the underlying row calculation implementation.
 * @param expandedRowIds The IDs of all rows that should be expanded (i.e. their children should be visible).
 *
 * @returns All calculated rows (some of which are not visible).
 */
export function calculateRows(
  topLevelRowGroup: RowGroup | RowGroup[],
  context: RowDerivationContext,
  expandedRowIds: readonly string[],
): RowWithMetadata[] {
  function* calculateRowsForDataRowAndDescendants(
    dataRow: DataRow<unknown>,
    isVisible: boolean,
    indentationLevel: number,
  ): Generator<RowWithMetadata> {
    const childRowGroups = getChildrenOfDataRow(dataRow, context);

    const expandable = childRowGroups.some((rowGroup) => getContainerRow(rowGroup) || getDataRows(rowGroup).length > 0);

    yield { row: dataRow, indentationLevel, expandable, isVisible };

    const rowIsExpanded = isVisible && expandedRowIds.includes(dataRow.rowId);

    for (const rowGroup of childRowGroups) {
      yield* calculateRowsForRowGroupAndDescendants(rowGroup, rowIsExpanded, indentationLevel + 1);
    }
  }

  function* calculateRowsForRowGroupAndDescendants(
    rows: RowGroup | RowGroup[],
    parentIsExpanded: boolean,
    indentationLevel: number,
  ): Generator<RowWithMetadata> {
    if (!Array.isArray(rows) && isRowGroupWithContainer(rows)) {
      if (rows.containerRow) {
        yield {
          row: rows.containerRow,
          indentationLevel,
          expandable: true,
          isVisible: parentIsExpanded,
        };
      }

      const dataRowIndentationLevel = rows.containerRow ? indentationLevel + 1 : indentationLevel;
      const dataRowIsVisible = rows.containerRow
        ? parentIsExpanded && expandedRowIds.includes(rows.containerRow.rowId)
        : parentIsExpanded;

      for (const dataRow of rows.dataRows) {
        yield* calculateRowsForDataRowAndDescendants(dataRow, dataRowIsVisible, dataRowIndentationLevel);
      }
    } else {
      for (const row of forceArray(rows)) {
        if (isArray(row) || isRowGroupWithContainer(row)) {
          yield* calculateRowsForRowGroupAndDescendants(row, parentIsExpanded, indentationLevel);
        } else {
          yield* calculateRowsForDataRowAndDescendants(row, parentIsExpanded, indentationLevel);
        }
      }
    }
  }

  return Array.from(calculateRowsForRowGroupAndDescendants(topLevelRowGroup, true, 0));
}

export class CachedRowCalculator {
  /**
   * Since this only remembers the rows from the last calculation, there should be no concern of the cache growing too
   *   large.
   */
  private cachedRowsWithMetadata = new Map<string, RowWithMetadata>();

  private getCachedRowWithMetadataIfRowEqual(row: Row, equalRowIdsSoFar: ReadonlySet<string>): RowWithMetadata | null {
    const cachedRowWithMetadata = this.cachedRowsWithMetadata.get(row.rowId);
    if (!cachedRowWithMetadata) return null;
    const cachedRow = cachedRowWithMetadata.row;

    // We can't check if the parents are referentially equal, since all rows are created from scratch, so instead we
    // keep track of which rows successfully used the cached row so far, with the assumption that parent rows will be
    // processed before their children.
    if (row.parent && cachedRow.parent && !equalRowIdsSoFar.has(row.parent.rowId)) return null;
    if (!!row.parent !== !!cachedRow.parent) return null;

    if (isContainerRow(row)) {
      if (!isContainerRow(cachedRow)) return null;

      if (row.childrenCount !== cachedRow.childrenCount) return null;
      if (row.childrenType !== cachedRow.childrenType) return null;

      return cachedRowWithMetadata;
    }

    if (isDataRow(row)) {
      if (!isDataRow(cachedRow)) return null;

      if (row.item !== cachedRow.item) return null;
      if (row.schema !== cachedRow.schema) return null;

      return cachedRowWithMetadata;
    }

    throw new Error('Unknown row type.');
  }

  /**
   * This function generates a flat array of all rows that can be calculated from the given `topLevelRowGroup`, along
   * with the metadata needed to render them easily with our grid implementations.
   *
   * @param topLevelRows The top level rows in the grid; all descendants will be calculated.
   * @param context The data needed by the underlying row calculation implementation.
   * @param expandedRowIds The IDs of all rows that should be expanded (i.e. their children should be visible).
   *
   * @returns All calculated rows (some of which are not visible).
   */
  calculateRows(topLevelRows: RowGroup | RowGroup[], context: RowDerivationContext, expandedRowIds: readonly string[]) {
    const rowsWithMetadata = calculateRows(topLevelRows, context, expandedRowIds);

    const rowsWithCaching: RowWithMetadata[] = [];
    const equalRowIds = new Set<string>();
    for (const rowWithMetadata of rowsWithMetadata) {
      const cachedRowWithMetadata = this.getCachedRowWithMetadataIfRowEqual(rowWithMetadata.row, equalRowIds);
      if (cachedRowWithMetadata) {
        equalRowIds.add(rowWithMetadata.row.rowId);

        if (rowMetadataEquals(cachedRowWithMetadata, rowWithMetadata)) {
          rowsWithCaching.push(cachedRowWithMetadata);
        } else {
          rowsWithCaching.push({ ...rowWithMetadata, row: cachedRowWithMetadata.row });
        }
      } else {
        rowsWithCaching.push(rowWithMetadata);
      }
    }

    this.cachedRowsWithMetadata = new Map<string, RowWithMetadata>(
      rowsWithCaching.map((rowWithMetadata) => [rowWithMetadata.row.rowId, rowWithMetadata]),
    );

    return rowsWithCaching;
  }
}
